import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import type { Message, VisitorConfig, FaqPublic } from '@chat/types';
import type { ServerEnvelope } from '@chat/protocol';
import { WsClient } from '../lib/ws';
import { buildWsUrl, initVisitor, refreshVisitor, uploadImage, VisitorApiError } from '../lib/api';
import { getVisitorIdentity, saveVisitorId } from '../lib/fingerprint';
import { createNotifyAudio, createTabTitleAlert } from '../lib/notify';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

/**
 * 把可能是相对路径的资源 URL 补齐为绝对 URL。
 * widget 嵌入到跨域站点时，`/r2/xxx.png` 会被浏览器解析成宿主页域名，
 * 但资源真正的存放地址是 widget 的 host（chat.toriri.io）。
 */
function resolveAssetUrl(host: string, url: string | null | undefined): string | null {
  if (!url) return null;
  if (url.startsWith('/')) return host + url;
  return url;
}

interface PanelReadyInfo {
  config: VisitorConfig;
  clickFaq: (faq: FaqPublic) => void;
  /** 供外部（chat-page）同步更新标签页原始标题 */
  updateTabTitleBase: (title: string) => void;
}

interface PanelProps {
  host: string;
  productSlug: string;
  onClose?: () => void;
  /** 'embedded' 右下角浮层（widget 默认）；'page' 全屏对话页 */
  mode?: 'embedded' | 'page';
  /**
   * 在 page 模式下，Panel 会通过此回调把 config（含 faqs）和 clickFaq 函数暴露出去，
   * 供外层容器渲染右侧 FAQ 侧栏。
   */
  onReady?: (info: PanelReadyInfo) => void;
}

interface LocalMessage extends Message {
  pending?: boolean;
}

type BootState =
  | { s: 'loading' }
  | { s: 'banned' }
  | { s: 'ready'; conversationId: string; visitorId: string; config: VisitorConfig }
  | { s: 'error'; message: string };

interface PendingAttachment {
  id: string;
  file: File;
  localUrl: string;
  status: 'uploading' | 'ready' | 'failed';
  r2Key?: string;
  mime?: string;
  size?: number;
}

export function Panel({ host, productSlug, onClose, mode = 'embedded', onReady }: PanelProps) {
  const [boot, setBoot] = useState<BootState>({ s: 'loading' });
  const [messages, setMessages] = useState<LocalMessage[]>([]);
  const [input, setInput] = useState('');
  const [pending, setPending] = useState<PendingAttachment[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const wsRef = useRef<WsClient | null>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const wsTokenRef = useRef<string>('');
  const bannedRef = useRef(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingByTempIdRef = useRef<Map<string, string>>(new Map());
  // 通知：音频总是启用；标签标题只在 page 模式（独立对话页）用，避免污染宿主页
  const notifyRef = useRef(createNotifyAudio(host));
  const tabAlertRef = useRef(mode === 'page' ? createTabTitleAlert() : null);

  // ---- 标签标题 + 音频初始化 ------------------------------------
  useEffect(() => {
    tabAlertRef.current?.install();
    // Panel 挂载时尝试一次 unlock（如果是通过点击气泡打开的，会成功）
    void notifyRef.current.unlock();
    return () => {
      tabAlertRef.current?.uninstall();
    };
  }, []);

  // ---- 初始化 -----------------------------------------------------
  useEffect(() => {
    if (bannedRef.current) return;
    let cancelled = false;
    (async () => {
      try {
        const identity = getVisitorIdentity();
        const init = await initVisitor(host, productSlug, identity.id, identity.hadStoredId);
        if (cancelled || bannedRef.current) return;

        // 后端可能通过唯一 IP 候选恢复了旧访客，保存后端确认的最终 ID
        saveVisitorId(init.visitorId);

        // 历史消息已在 init 响应里，省一次 RTT
        setMessages(init.messages);
        wsTokenRef.current = init.wsToken;
        setBoot({
          s: 'ready',
          conversationId: init.conversationId,
          visitorId: init.visitorId,
          config: init.config,
        });
        // 暴露给外层容器（page 模式渲染 FAQ 侧栏用）
        // 用 stable wrapper 避免 stale closure（外层持有的引用永远指向最新逻辑）
        onReady?.({
          config: init.config,
          clickFaq: clickFaqStable.current,
          updateTabTitleBase: (t) => tabAlertRef.current?.updateBase(t),
        });

        // 建 WebSocket
        const ws = new WsClient({
          url: buildWsUrl(host, init.wsToken),
          onMessage: handleServerMessage,
          onBanned: enterBanned,
          onReconnectFailed: () => {
            if (!cancelled && !bannedRef.current) setBoot({ s: 'error', message: '重连失败' });
          },
          beforeReconnect: async () => {
            try {
              const refreshed = await refreshVisitor(host, wsTokenRef.current);
              if (cancelled || bannedRef.current) return null;
              // 重连仅刷新当前身份，不把旧队列发送给其他会话。
              if (refreshed.visitorId !== init.visitorId || refreshed.conversationId !== init.conversationId) {
                setBoot({ s: 'error', message: '会话已失效' });
                return null;
              }
              wsTokenRef.current = refreshed.wsToken;
              return buildWsUrl(host, refreshed.wsToken);
            } catch (e) {
              if (cancelled || bannedRef.current) return null;
              if (e instanceof VisitorApiError) {
                if (e.code === 'banned') { enterBanned(); return null; }
                if (e.status >= 400 && e.status < 500 && e.status !== 429) {
                  setBoot({ s: 'error', message: e.message });
                  return null;
                }
              }
              throw e;
            }
          },
        });
        wsRef.current = ws;
        ws.connect();
      } catch (e) {
        if (!cancelled) {
          if (e instanceof VisitorApiError && e.code === 'banned') enterBanned();
          else setBoot({ s: 'error', message: (e as Error).message });
        }
      }
    })();
    return () => {
      cancelled = true;
      wsRef.current?.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [host, productSlug]);

  // ---- 消息滚动到底部 ---------------------------------------------
  useEffect(() => {
    const el = bodyRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  // ---- 服务端消息 -------------------------------------------------
  function enterBanned() {
    bannedRef.current = true;
    wsRef.current?.close();
    wsTokenRef.current = '';
    pendingByTempIdRef.current.clear();
    setPending((prev) => {
      for (const p of prev) URL.revokeObjectURL(p.localUrl);
      return [];
    });
    setMessages((prev) => prev.filter((m) => !m.pending));
    setInput('');
    setUploadError(null);
    setBoot({ s: 'banned' });
  }

  function handleServerMessage(msg: ServerEnvelope) {
    if (bannedRef.current) return;
    if (msg.t === 'error' && msg.code === 'banned') { enterBanned(); return; }
    if (msg.t === 'ack') {
      const realId = msg.messageId;
      pendingByTempIdRef.current.set(msg.tempId, realId);
      setMessages((prev) =>
        prev.map((m) =>
          m.id === msg.tempId
            ? { ...m, id: realId, pending: false, createdAt: msg.createdAt }
            : m,
        ),
      );
      return;
    }
    if (msg.t === 'msg') {
      const incoming = msg.message;
      // 去重：ack 已经处理过的自己消息不重复添加
      setMessages((prev) => {
        if (prev.some((m) => m.id === incoming.id)) return prev;
        return [...prev, incoming];
      });
      // 收到对方消息（客服或 AI）→ 提示音 + 标签标题
      if (incoming.role !== 'visitor') {
        void notifyRef.current.play();
        tabAlertRef.current?.alert('有新消息');
      }
      return;
    }
    if (msg.t === 'error') {
      // 只对访客有意义的错误显示提示，其它静默
      if (msg.code === 'rate_limited') setUploadError('发送过快，稍等一下');
      else if (msg.code === 'too_long') setUploadError('消息过长');
      else if (msg.code === 'bad_message') setUploadError('消息格式错误，请刷新重试');
      // 3 秒后自动清除
      setTimeout(() => setUploadError(null), 3000);
      return;
    }
    // typing / state / pong 忽略（用户侧不做提示）
  }

  // ---- 发送消息 ---------------------------------------------------
  function sendMessage() {
    // 发送本身是 user activation，再 unlock 一次（幂等，已解锁会立即返回）
    void notifyRef.current.unlock();
    if (bannedRef.current || boot.s !== 'ready') return;
    const trimmed = input.trim();
    const readyAtts = pending.filter((p) => p.status === 'ready');
    if (!trimmed && readyAtts.length === 0) return;

    const ws = wsRef.current;
    if (!ws) return;

    const tempId = `tmp_${Math.random().toString(36).slice(2, 10)}`;
    const attachments = readyAtts.length > 0
      ? readyAtts.map((p) => ({ r2Key: p.r2Key!, mime: p.mime!, size: p.size! }))
      : null;

    const optimistic: LocalMessage = {
      id: tempId,
      conversationId: boot.conversationId,
      role: 'visitor',
      content: trimmed,
      attachments,
      meta: null,
      createdAt: Date.now(),
      pending: true,
    };
    setMessages((prev) => [...prev, optimistic]);
    ws.send({
      t: 'msg',
      content: trimmed,
      tempId,
      ...(attachments && { attachments }),
    });

    // 清空输入 + 释放 blob
    setInput('');
    for (const p of readyAtts) URL.revokeObjectURL(p.localUrl);
    // 只保留仍在上传或失败的（不该发的）
    setPending((prev) => prev.filter((p) => p.status !== 'ready'));
  }

  // clickFaq 会被外部（chat-page 右侧栏）通过 onReady 回调持有引用。
  // 为避免 stale closure，用 ref 持有最新版本，稳定 wrapper 通过 ref 转发。
  const clickFaqLatestRef = useRef<(faq: FaqPublic) => void>(() => {});
  clickFaqLatestRef.current = function clickFaq(faq: FaqPublic) {
    // FAQ 点击也是 user activation
    void notifyRef.current.unlock();
    if (bannedRef.current || boot.s !== 'ready') return;
    const ws = wsRef.current;
    if (!ws) return;

    const tempId = `tmp_${Math.random().toString(36).slice(2, 10)}`;
    const optimistic: LocalMessage = {
      id: tempId,
      conversationId: boot.conversationId,
      role: 'visitor',
      content: faq.question,
      attachments: null,
      meta: { source: 'faq', faqId: faq.id },
      createdAt: Date.now(),
      pending: true,
    };
    setMessages((prev) => [...prev, optimistic]);
    ws.send({ t: 'faq', faqId: faq.id, tempId });
  };
  const clickFaqStable = useRef((faq: FaqPublic) => clickFaqLatestRef.current(faq));

  function handleKeyDown(e: KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey && !e.isComposing) {
      e.preventDefault();
      if (canSend) sendMessage();
    }
  }

  /**
   * 把图片加入待发队列，后台开始上传，UI 立即在输入框上方显示缩略图。
   * 用户点发送或按 Enter 时才随文字一起发出。
   */
  function queueImage(file: File) {
    if (bannedRef.current || boot.s !== 'ready') return;
    setUploadError(null);

    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setUploadError('只支持 jpg/png/gif/webp');
      return;
    }
    if (file.size > MAX_IMAGE_SIZE) {
      setUploadError('图片不能超过 5MB');
      return;
    }

    const id = `p_${Math.random().toString(36).slice(2, 10)}`;
    const localUrl = URL.createObjectURL(file);
    setPending((prev) => [...prev, { id, file, localUrl, status: 'uploading' }]);

    // 后台上传
    uploadImage(host, wsTokenRef.current, file)
      .then(({ r2Key, mime, size }) => {
        if (bannedRef.current) return;
        setPending((prev) =>
          prev.map((p) =>
            p.id === id ? { ...p, status: 'ready', r2Key, mime, size } : p,
          ),
        );
      })
      .catch((e) => {
        if (bannedRef.current) return;
        if (e instanceof VisitorApiError && e.code === 'banned') { enterBanned(); return; }
        setPending((prev) =>
          prev.map((p) => (p.id === id ? { ...p, status: 'failed' } : p)),
        );
      });
  }

  function removePending(id: string) {
    setPending((prev) => {
      const target = prev.find((p) => p.id === id);
      if (target) URL.revokeObjectURL(target.localUrl);
      return prev.filter((p) => p.id !== id);
    });
  }

  function handleFilePick(e: Event) {
    const inputEl = e.target as HTMLInputElement;
    const files = Array.from(inputEl.files ?? []);
    for (const file of files) queueImage(file);
    inputEl.value = ''; // 允许重选同一文件
  }

  function handlePaste(e: ClipboardEvent) {
    const items = e.clipboardData?.items;
    if (!items) return;
    let hasImage = false;
    for (const item of Array.from(items)) {
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) {
          hasImage = true;
          queueImage(file);
        }
      }
    }
    // 如果粘贴的是图片，阻止 textarea 插入图片的 URL/占位
    if (hasImage) e.preventDefault();
  }

  const hasReadyPending = pending.some((p) => p.status === 'ready');
  const hasUploadingPending = pending.some((p) => p.status === 'uploading');
  const canSend =
    boot.s === 'ready'
    && !hasUploadingPending
    && (input.trim().length > 0 || hasReadyPending);

  // ---- 渲染 -------------------------------------------------------
  const brandColor = boot.s === 'ready' ? boot.config.brandColor : '#07C160';
  const rootStyle = useMemo(
    () => ({ '--brand': brandColor } as Record<string, string>),
    [brandColor],
  );

  // 加载/错误态：只显示居中占位，藏掉头部/消息区/FAQ/输入区
  if (boot.s !== 'ready') {
    return (
      <div class={`ct-panel ct-panel-${mode}`} style={rootStyle}>
        {onClose && (
          <button class="ct-close ct-close-abs" onClick={onClose} aria-label="关闭">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M6 6l12 12M6 18L18 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
            </svg>
          </button>
        )}
        <div class="ct-connecting">
          {boot.s === 'loading' ? (
            <>
              <div class="ct-dots">
                <span /><span /><span />
              </div>
              <div class="ct-connecting-text">正在为您接入客服…</div>
            </>
          ) : (
            <>
              <div class="ct-connecting-err">{boot.s === 'banned' ? '暂时无法使用客服' : '连接失败'}</div>
              <div class="ct-connecting-sub">{boot.s === 'banned' ? '当前访问已被封禁，无法发送消息。' : '请检查网络后重新打开'}</div>
            </>
          )}
        </div>
      </div>
    );
  }

  // ready 状态才有完整 UI
  return (
    <div class={`ct-panel ct-panel-${mode} ct-panel-ready`} style={rootStyle}>
      <div class="ct-header">
        <div class="ct-header-avatar">
          {boot.config.agentAvatar ? (
            <img src={resolveAssetUrl(host, boot.config.agentAvatar) ?? ''} alt="" />
          ) : (
            boot.config.agentName.slice(0, 1)
          )}
        </div>
        <div class="ct-header-info">
          <div class="ct-header-name">
            <span class="ct-header-dot" />
            客服 - {boot.config.agentName}
          </div>
          {boot.config.agentTitle && (
            <div class="ct-header-title">{boot.config.agentTitle}</div>
          )}
        </div>
        {onClose && (
          <button class="ct-close" onClick={onClose} aria-label="关闭">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M6 6l12 12M6 18L18 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
            </svg>
          </button>
        )}
      </div>

      <div class="ct-body" ref={bodyRef}>
        {boot.config.welcomeMessage && messages.length === 0 && (
          <div class="ct-welcome">{boot.config.welcomeMessage}</div>
        )}
        {messages.map((m, idx) => (
          <MessageRow
            key={m.id}
            host={host}
            msg={m}
            prev={idx > 0 ? messages[idx - 1] ?? null : null}
            agentAvatar={resolveAssetUrl(host, boot.config.agentAvatar)}
            agentName={boot.config.agentName}
          />
        ))}
      </div>

      {boot.config.faqs.length > 0 && mode === 'embedded' && (
        <div class="ct-faq">
          <span class="ct-faq-label">常见问题</span>
          {boot.config.faqs.map((f) => (
            <span class="ct-faq-pill" key={f.id} onClick={() => clickFaqLatestRef.current(f)}>
              {f.question}
            </span>
          ))}
        </div>
      )}

      {uploadError && <div class="ct-upload-error">{uploadError}</div>}

      {pending.length > 0 && (
        <div class="ct-pending">
          {pending.map((p) => (
            <div class={`ct-pending-item status-${p.status}`} key={p.id}>
              <img src={p.localUrl} alt="" />
              {p.status === 'uploading' && <div class="ct-pending-overlay">上传中…</div>}
              {p.status === 'failed' && <div class="ct-pending-overlay failed">上传失败</div>}
              <button class="ct-pending-remove" onClick={() => removePending(p.id)} aria-label="移除">×</button>
            </div>
          ))}
        </div>
      )}

      <div class="ct-input">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp"
          multiple
          style={{ display: 'none' }}
          onChange={handleFilePick}
        />
        <button
          class="ct-attach"
          type="button"
          aria-label="发送图片"
          title="发送图片"
          onClick={() => fileInputRef.current?.click()}
        >
          <svg viewBox="0 0 24 24">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor" stroke="none" />
            <path d="M21 15l-5-5L5 21" />
          </svg>
        </button>
        <textarea
          class="ct-textarea"
          placeholder="输入消息…"
          value={input}
          onInput={(e) => setInput((e.target as HTMLTextAreaElement).value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onFocus={() => void notifyRef.current.unlock()}
          rows={1}
        />
        <button
          class={`ct-send${canSend ? ' active' : ''}`}
          type="button"
          aria-label="发送"
          title={hasUploadingPending ? '图片上传中，请稍等' : '发送 (Enter)'}
          disabled={!canSend}
          onClick={() => { if (canSend) sendMessage(); }}
        >
          <svg viewBox="0 0 24 24">
            <path d="M22 2L11 13" />
            <path d="M22 2l-7 20-4-9-9-4 20-7z" />
          </svg>
        </button>
      </div>
    </div>
  );
}

function MessageRow({
  host,
  msg,
  prev,
  agentAvatar,
  agentName,
}: {
  host: string;
  msg: LocalMessage;
  prev: LocalMessage | null;
  agentAvatar: string | null;
  agentName: string;
}) {
  const isSelf = msg.role === 'visitor';
  const isSystem = msg.role === 'system';
  const sameAsPrev = prev && prev.role === msg.role;
  const showTime = !prev || msg.createdAt - prev.createdAt > 5 * 60 * 1000;

  const hasImages = msg.attachments && msg.attachments.length > 0;
  const hasText = msg.content && msg.content.trim().length > 0;

  // 系统消息（警告/封禁提示）：单独居中样式，无头像
  if (isSystem) {
    return (
      <>
        {showTime && <div class="ct-msg-time">{formatTime(msg.createdAt)}</div>}
        <div class="ct-msg-system">{msg.content}</div>
      </>
    );
  }

  return (
    <>
      {showTime && <div class="ct-msg-time">{formatTime(msg.createdAt)}</div>}
      <div class={`ct-msg${isSelf ? ' self' : ''}${sameAsPrev ? ' same' : ''}`}>
        <div class={`ct-msg-avatar${sameAsPrev ? ' hidden' : ''}${isSelf ? ' self' : ''}`}>
          {isSelf
            ? <img src={`${host}/visitor-avatar.svg`} alt="" />
            : agentAvatar
              ? <img src={agentAvatar} alt="" />
              : agentName.slice(0, 1)}
        </div>
        <div class={`ct-msg-content${!hasText && hasImages ? ' image-only' : ''}`}>
          {hasImages && msg.attachments!.map((att) => {
            const src = att.r2Key.startsWith('__local:')
              ? att.r2Key.slice('__local:'.length)
              : `${host}/r2/${att.r2Key}`;
            return (
              <a
                class="ct-msg-image-link"
                href={src}
                target="_blank"
                rel="noopener noreferrer"
                key={att.r2Key}
              >
                <img class="ct-msg-image" src={src} alt="" loading="lazy" />
              </a>
            );
          })}
          {hasText && <div class="ct-msg-bubble">{msg.content}</div>}
        </div>
      </div>
    </>
  );
}

function formatTime(ts: number): string {
  const d = new Date(ts);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  const hm = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  if (isToday) return hm;
  return `${d.getMonth() + 1}/${d.getDate()} ${hm}`;
}

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}
